import React, { useEffect, useState } from 'react';
import DashboardIcon from '../icons/dashboard_icon';
import AssessmentsIcon from '../icons/assessments_icon';
import LearningIcon from '../icons/learning_icon';
import ManagementIcon from '../icons/management_icon';
import NewsFeedIcon from '../icons/news_feed_icon';
import { useNavigate, useLocation } from 'react-router-dom';
import { getItem, removeItem, setItem } from '../../../utils/localStorage';
import './BottomNavBar.scss';
import UnsavedFormDataModal from '../UnsavedFormDataModal';
import {
  Wrapper,
  NavContainer,
  NavItem,
  CustomNavItem,
  CustomIconContainer,
  IconWrapper,
  IconLabel,
} from './BottomNavBar.styled';

const defaultValues = {
  dashboard: false,
  assessments: false,
  myactions: false,
  newsfeed: false,
  learning: false,
  notifications: false,
  profile: false,
  none: false,
};

const BottomNavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [navbarItems, setNavbarItems] = useState(defaultValues);
  const [selectedNavItem, setSelectedNavItem] = useState<string>('');
  const [showUnsavedDataModal, setShowUnsavedDataModal] =
    useState<boolean>(false);

  const setActiveNavBarItem = (newValue: string) => {
    const navKeys = Object.keys(defaultValues);
    let sanitizedNewKeyValue = newValue.split('/')[0];
    if (!sanitizedNewKeyValue.length) {
      sanitizedNewKeyValue = 'dashboard';
    }
    const keyToSetActive = navKeys.includes(sanitizedNewKeyValue)
      ? sanitizedNewKeyValue
      : 'none';
    const resetedValues = {
      ...defaultValues,
      [keyToSetActive]: true,
    };
    setNavbarItems(resetedValues);
  };

  const addListenerForExternalNavigationPath = () => {
    window.addEventListener('storage', (e) => {
      const externalNavigationPath = getItem('externalNavigationPath');
      if (!externalNavigationPath || externalNavigationPath.length === 0) {
        return;
      }
      navBarItemHandler(externalNavigationPath);
      setItem('externalNavigationPath', '');
    });
  };

  useEffect(() => {
    addListenerForExternalNavigationPath();
    const pathName = location.pathname;
    setActiveNavBarItem(pathName.replace('/', ''));
  }, []);

  const navBarItemHandler = (value: string) => {
    navigate(`/${value}`);
    setActiveNavBarItem(value);
    removeItem('saFilter');
    if (getItem('selfAssessments')) {
      removeItem('selfAssessments');
    }
    if (getItem('currentSelfAssessmentIndex')) {
      removeItem('currentSelfAssessmentIndex');
    }
  };

  useEffect(() => {
    const pathName = location.pathname;
    if (location.pathname === '/pilotwelcome') {
      setActiveNavBarItem(pathName.replace('/', ''));
    }
    setSelectedNavItem('');
  }, [location.pathname]);

  useEffect(() => {
    if (!selectedNavItem) {
      return;
    }
    if (getItem('isFormData')) {
      setShowUnsavedDataModal(true);
      return;
    }
    navBarItemHandler(selectedNavItem);
  }, [selectedNavItem]);

  return (
    <Wrapper className="nav-wrapper">
      {showUnsavedDataModal && (
        <UnsavedFormDataModal
          showUnsavedDataModal={showUnsavedDataModal}
          setShowUnsavedDataModal={setShowUnsavedDataModal}
          onContinue={() => navBarItemHandler(selectedNavItem)}
          onCancel={() => setSelectedNavItem('')}
        />
      )}
      <NavContainer className="nav-container">
        <NavItem onClick={() => setSelectedNavItem('dashboard')}>
          <IconWrapper>
            <DashboardIcon size={26} active={navbarItems.dashboard} />
          </IconWrapper>
          <IconLabel active={navbarItems.dashboard}>Dashboard</IconLabel>
        </NavItem>

        <NavItem onClick={() => setSelectedNavItem('assessments')}>
          <IconWrapper>
            <AssessmentsIcon size={26} active={navbarItems.assessments} />
          </IconWrapper>
          <IconLabel active={navbarItems.assessments}>Assessments</IconLabel>
        </NavItem>

        <NavItem
          className="customNavItem"
          onClick={() => setSelectedNavItem('myactions')}
        >
          <CustomNavItem className={`customNavBar`} data-testid="custom-nav-item">
            <CustomIconContainer
              className={`customIconContainer ${
                navbarItems.myactions ? 'active' : ''
              }`}
            >
              <ManagementIcon size={32} active={navbarItems.myactions} />
            </CustomIconContainer>
          </CustomNavItem>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="4"
            height="4"
            viewBox="0 0 4 4"
            fill="none"
          />
          <span className="customNavText">
            <IconLabel active={navbarItems.myactions}>
              <span className="font-italic">my</span>
              <span>Actions</span>
            </IconLabel>
          </span>
        </NavItem>

        <NavItem onClick={() => setSelectedNavItem('newsfeed')}>
          <IconWrapper>
            <NewsFeedIcon size={26} active={navbarItems.newsfeed} />
          </IconWrapper>
          <IconLabel active={navbarItems.newsfeed}>Newsfeed</IconLabel>
        </NavItem>

        <NavItem onClick={() => setSelectedNavItem('learning')}>
          <IconWrapper>
            <LearningIcon size={28} active={navbarItems.learning} />
          </IconWrapper>
          <IconLabel active={navbarItems.learning}>Learning</IconLabel>
        </NavItem>
      </NavContainer>
    </Wrapper>
  );
};

export default BottomNavBar;
